import type { CSSProperties, ReactNode } from 'react';
import { PlainLink, StatusDot, type LinkComponent } from '@paige/ui';
import { useFormat } from '@paige/format';
import { partName } from '../pools/pools-words';
import { FlowSpark } from './FlowGlyph';
import { busyWord, placeName, poolName } from './flow-words';
import type { FlowCard, FlowEngineHost, FlowPart, FlowPlace, FlowPool } from './flow-types';

/** One physical card and the pools that used it. */
export interface CardUse {
  card: string;
  parts: FlowCard[];
  busy: number;
  series: number[];
}

/** The cards a set of pools ran on: the page reader and understanding share the reading cards. */
export function cardsOf(cards: FlowCard[], pools: FlowPool[]): CardUse[] {
  const byCard = new Map<string, FlowCard[]>();
  for (const c of cards.filter((x) => pools.includes(x.pool))) {
    byCard.set(c.card, [...(byCard.get(c.card) ?? []), c]);
  }
  return [...byCard.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([card, parts]) => ({
      card,
      parts,
      busy: Math.min(
        1,
        parts.reduce((n, p) => n + p.busy, 0),
      ),
      series: parts[0]!.series.map((_, i) =>
        Math.min(
          1,
          parts.reduce((n, p) => n + (p.series[i] ?? 0), 0),
        ),
      ),
    }));
}

/**
 * The card's last five minutes as one bar, a segment per pool, the rest free. Pools and releases shows one
 * pool at a time under its own name, so it leaves the legend out.
 */
export function UseBar({ use, legend = true }: { use: CardUse; legend?: boolean }): ReactNode {
  const f = useFormat();
  const word = busyWord(use.busy);
  return (
    <div className={`fl-card fl-card--${word.toLowerCase()}`}>
      <div className="fl-card__top">
        <strong className="fl-card__word">{word}</strong>
        <span className="fl-card__pct">{f.percent(Math.round(use.busy * 100))} busy</span>
      </div>
      <div
        className="fl-card__bar"
        role="meter"
        aria-label="Card busy"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(use.busy * 100)}
      >
        {use.parts.map((p) => (
          <span
            key={p.pool}
            className={`fl-card__seg fl-card__seg--${p.pool}`}
            style={{ '--fl-share': `${(Math.min(1, p.busy) * 100).toFixed(1)}%` } as CSSProperties}
          />
        ))}
      </div>
      {legend ? (
        <div className="fl-card__parts">
          {use.parts.map((p) => (
            <span key={p.pool} className={`fl-card__part fl-card__part--${p.pool}`}>
              {poolName[p.pool]} {f.percent(Math.round(p.busy * 100))}
            </span>
          ))}
        </div>
      ) : null}
      <FlowSpark values={use.series} label="Card busy over 15 minutes" />
    </div>
  );
}

/** The pools and services a group of cards stands for, for lighting it when one is not up. */
const GROUP_PARTS: Record<string, string[]> = {
  'Page preparation': ['page-prep', 'page-work'],
  'Reading cards': ['ocr', 'understanding'],
  'Reasoner card': ['reasoner'],
  'Engine host': ['engine-host'],
};

type Light = ((places: string[]) => void) | undefined;

function Group({
  title,
  note,
  children,
  parts = [],
  onLight,
}: {
  title: string;
  note?: ReactNode;
  children: ReactNode;
  parts?: FlowPart[];
  onLight?: Light;
}): ReactNode {
  const hurt = parts.filter(
    (p) => (GROUP_PARTS[title] ?? []).includes(p.id) && (p.state === 'down' || p.state === 'short'),
  );
  const state = hurt.some((p) => p.state === 'down') ? 'down' : hurt.length > 0 ? 'short' : null;
  const starves = hurt.flatMap((p) => p.starves);
  return (
    <div
      className={state ? `fl-cards__group fl-cards__group--${state}` : 'fl-cards__group'}
      onMouseEnter={starves.length > 0 ? () => onLight?.(starves) : undefined}
      onMouseLeave={starves.length > 0 ? () => onLight?.([]) : undefined}
    >
      <div className="fl-cards__title">
        <span>{title}</span>
        {note ? <span className="fl-cards__note">{note}</span> : null}
      </div>
      <div className="fl-cards__row">{children}</div>
    </div>
  );
}

export const Idle = ({ what }: { what: string }): ReactNode => (
  <div className="fl-card fl-card--idle">
    <div className="fl-card__top">
      <strong className="fl-card__word">Idle</strong>
      <span className="fl-card__pct">{what}</span>
    </div>
    <div className="fl-card__bar" />
  </div>
);

export function Preparation({ cards }: { cards: FlowCard[] }): ReactNode {
  const f = useFormat();
  const asks = cards.reduce((n, c) => n + c.asksPerMinute, 0);
  if (cards.length === 0 || asks === 0) return <Idle what="no pages in 5 min" />;
  return (
    <div className="fl-card fl-card--busy">
      <div className="fl-card__top">
        <strong className="fl-card__word">Working</strong>
        <span className="fl-card__pct">{f.decimal(asks, 0)} pages/min</span>
      </div>
      <FlowSpark
        values={cardsOf(cards, ['page-prep'])[0]?.series ?? []}
        label="Page preparation over 15 minutes"
      />
    </div>
  );
}

/** Each card's use, or that none was asked of in a quarter hour. */
function Uses({ uses }: { uses: CardUse[] }): ReactNode {
  if (uses.length === 0) return <Idle what="no asks in 15 min" />;
  return uses.map((u) => <UseBar key={u.card} use={u} />);
}

/** A pool or service that is not up, with the stages it holds back, and the way to Pools and releases. */
function Trouble({
  parts,
  Link,
  onLight,
}: {
  parts: FlowPart[];
  Link: LinkComponent;
  onLight: Light;
}): ReactNode {
  const down = parts.filter((p) => p.state === 'down' || p.state === 'short');
  if (down.length === 0) return null;
  return (
    <p className="fl-cards__trouble" role="status">
      {down.map((p) => (
        <button
          type="button"
          key={p.id}
          className={`fl-chip fl-chip--${p.state === 'down' ? 'stuck' : 'slow'} fl-chip--part`}
          onMouseEnter={() => onLight?.(p.starves)}
          onMouseLeave={() => onLight?.([])}
          onFocus={() => onLight?.(p.starves)}
          onBlur={() => onLight?.([])}
        >
          {partName(p.id)}: {p.ready} of {p.wanted} ready
          {p.starves.length > 0
            ? `, holds back ${p.starves.map((s) => placeName[s as FlowPlace] ?? s).join(', ')}`
            : ''}
        </button>
      ))}{' '}
      <Link href="/admin/pools">Open Pools and releases</Link>
    </p>
  );
}

function Host({ host }: { host: FlowEngineHost | null }): ReactNode {
  const f = useFormat();
  if (!host) return <Idle what="no engine host here" />;
  return (
    <div className={host.answering ? 'fl-card fl-card--host' : 'fl-card fl-card--down'}>
      <div className="fl-card__top">
        <strong className="fl-card__word">
          <StatusDot
            tone={host.answering ? 'success' : 'danger'}
            size="sm"
            label={host.answering ? 'Answering' : 'Not answering'}
          />{' '}
          {host.answering ? 'Answering' : 'Not answering'}
        </strong>
      </div>
      <div className="fl-card__parts">
        <span>{f.int(host.readings)} reading now</span>
        <span>
          {f.int(host.reviewRounds)} AI review {host.reviewRounds === 1 ? 'round' : 'rounds'}
          {host.reviewDocuments > 0 ? `, ${f.int(host.reviewDocuments)} documents` : ''}
        </span>
      </div>
    </div>
  );
}

/**
 * The machinery under the pipeline: page preparation (processors), the reading cards the page reader and
 * understanding share, the reasoner's card, and the engine host. A card says Full, Busy, Light or Idle from the
 * share of the last five minutes its calls held it: measured from calls that have ended, so half a minute behind.
 */
export function FlowCardsStrip({
  cards,
  host,
  parts = [],
  Link = PlainLink,
  testsHidden = false,
  onLight,
}: {
  cards: FlowCard[];
  host: FlowEngineHost | null;
  /** The pools and services as paige-watch last recorded them; those not up are named above the cards. */
  parts?: FlowPart[];
  Link?: LinkComponent | undefined;
  /** Hide test companies is on: the cards are shared, so their use still counts every company. */
  testsHidden?: boolean | undefined;
  /** Points at the stages a part that is not up holds back, while it is hovered; empty to stop. */
  onLight?: Light;
}): ReactNode {
  const group = { parts, onLight };
  return (
    <section className="fl-cards" aria-label="Cards and hosts">
      <Trouble parts={parts} Link={Link} onLight={onLight} />
      <Group title="Page preparation" {...group}>
        <Preparation cards={cards.filter((c) => c.pool === 'page-prep')} />
      </Group>
      <Group title="Reading cards" note="page reader and understanding" {...group}>
        <Uses uses={cardsOf(cards, ['ocr', 'understanding'])} />
      </Group>
      <Group title="Reasoner card" note="AI review and job setup" {...group}>
        <Uses uses={cardsOf(cards, ['reasoner'])} />
      </Group>
      <Group title="Engine host" {...group}>
        <Host host={host} />
      </Group>
      <p className="fl-cards__foot">
        Card use is measured from finished asks, about half a minute behind
        {testsHidden
          ? ', and counts every company: the cards are shared, test companies included.'
          : '.'}
      </p>
    </section>
  );
}
